// Per-Gaussian data for the static-PLY hair renderer.
//
// All buffers (positions, albedo, fDec, normals, …) live here. HairRenderer
// holds a single HairData instance and the sibling modules (matMod, specMod,
// shade, envInstall) operate on it directly — they no longer reach into the
// orchestrator for per-Gaussian state.
//
// Lifecycle:
//   1. `new HairData(manifest, sidecar)` — dims + roughness/baseNormal from
//      load, and pre-allocate every other per-Gaussian buffer.
//   2. `data.setPositionsFromPackedSplats(packedSplats)` — fill positions +
//      centroid from the loaded PLY.
//   3. `unpackMatMod(matRes, data)` — fill albedo / fDec / fDecPad (one shot
//      at init; const_latent makes the output static).
//   4. Per frame: `runSpecMod`, `shadeFrame`, `writeDebugColors`,
//      `runNeuralBsdfOnce` mutate normals / specVis / outColors / outDiffuse
//      / outSpecular / neuralBsdfReflectance in place.
//
// Buffer ownership is local so we can later swap the JS-side TypedArrays for
// a wasm-heap view (the wasm-followup note in matMod.js) by only touching
// this file.

import * as THREE from 'three';
import { extractPositionsFromPackedSplats } from './splatUtil.js';
import { activeSpecGrid } from './specGrid.js';
import { unpackMatMod } from './matMod.js';
import { runSpecMod } from './specMod.js';
import { runNeuralBsdfOnce } from './shade.js';

const VIEW_FEAT_DIM = 8;

export class HairData {
  constructor(manifest, sidecar) {
    this.P = manifest.P;
    this.S = manifest.S;
    this.N = manifest.N;
    this.sPad = manifest.s_pad;
    this.nPad = manifest.n_pad;

    // The tight grid spec_mod runs on when the loaded asset takes dynamic dims
    // (OnnxRunner.specCropActive decides — the pack, not this manifest, is what
    // makes the crop legal). Pure function of S/N, so it's computed either way and
    // simply goes unread on a padded-grid pack. See specGrid.js.
    const { sG, nG } = activeSpecGrid(this, true);
    this.sSpec = sG;
    this.nSpec = nG;
    this.planeSpec = sG * nG;

    if (sidecar.P !== this.P) {
      console.warn(`[hair] sidecar P=${sidecar.P} vs manifest P=${this.P}; using manifest`);
    }
    this.roughness = sidecar.roughness;     // Float32Array(P)
    this.baseNormal = sidecar.baseNormal;   // Float32Array(P*3)

    const { P, sPad, nPad } = this;
    this.positions = null;                                    // filled in setPositionsFromPackedSplats
    this.albedo = new Float32Array(P * 3);                    // populated by unpackMatMod
    this.fDec = new Float32Array(P * 32);                     // populated by unpackMatMod
    this.fDecPad = new Float32Array(32 * sPad * nPad);        // populated by unpackMatMod
    this.normals = new Float32Array(P * 3);                   // base_normal + d_n
    this.specVis = new Float32Array(P);
    this.outColors = new Float32Array(P * 3);                 // shading output (final)
    this.outDiffuse = new Float32Array(P * 3);                // diffuse term only
    this.outSpecular = new Float32Array(P * 3);               // specular term only
    this.neuralBsdfReflectance = new Float32Array(P * 3).fill(1.0);
    this.specIn = new Float32Array((32 + VIEW_FEAT_DIM) * sPad * nPad);
    this.specOut = null;                                      // owned by runSpecMod's runner result

    // Until spec_mod runs we treat normals as base_normal and use a neutral
    // spec_vis — keeps the first shade well-formed even when useSpecMod=false
    // or when spec_mod hasn't been kicked off yet.
    this.normals.set(this.baseNormal);
    this.specVis.fill(0.5);

    this.centroid = new THREE.Vector3();
  }

  setPositionsFromPackedSplats(packedSplats) {
    const { positions, centroid } = extractPositionsFromPackedSplats(packedSplats, this.P);
    this.positions = positions;
    this.centroid.copy(centroid);
    return this.centroid;
  }

  // Populate albedo / fDec / fDecPad from a single mat_mod run. Called
  // once at init since const_latent makes the output static.
  setMaterial(matRes) {
    unpackMatMod(matRes.matUv, matRes.dims, this);
  }

  // Run view_enc + spec_mod for the current camera direction and refresh
  // `normals` (base_normal + d_n) + `specVis` in place. `camPos` is in
  // splat-local space; the caller (HairRenderer) owns that conversion
  // since it knows the camera + splat matrices.
  async setSpecular(runner, camPos) {
    await runSpecMod({ runner, data: this, camPos });
  }

  // Revert `normals` to `baseNormal` and `specVis` to neutral 0.5 — used
  // when useSpecMod=false or as a fallback when spec_mod throws.
  resetSpecular() {
    this.normals.set(this.baseNormal);
    this.specVis.fill(0.5);
  }

  // Run NeuralBSDF once with the supplied shCoeff48 and write the result
  // into `neuralBsdfReflectance` (identity-fills on failure, see shade.js).
  async setNeuralReflectance(runner, shCoeff48) {
    return runNeuralBsdfOnce(runner, shCoeff48, this);
  }

  // Reset reflectance to identity (1.0) — used by env-install paths that
  // failed to capture a probe and want shading to fall back to plain
  // albedo×env.
  resetNeuralReflectance() {
    this.neuralBsdfReflectance.fill(1);
    // Drop any gpu-resident bsdf output so the shade rebinds the CPU identity
    // array; bump the version so the (re)upload actually happens.
    this._reflGpuBuffer = null;
    this._reflVersion = (this._reflVersion ?? 0) + 1;
  }
}
